import type { ReactNode } from "react";

/**
 * The shared table's whole configuration surface (Code Map: "configured by
 * a column list and nothing else"). `key` is constrained to
 * `keyof Row & string` — the generated API type's own field names — which
 * is what makes a column naming a nonexistent field a compile error rather
 * than a runtime one (VC-2.2-03; the compile-time proof lives in
 * `table/__fixtures__/invalid-column-key.ts`).
 *
 * There is deliberately no `label` field here: display names come from
 * exactly one label map (`lib/field-labels.ts`, looked up by `key`), so a
 * column config can never carry its own duplicate header string
 * (VC-2.2-04).
 *
 * Phone's two-line row (DESIGN.md § Layout & Spacing) is driven by column
 * *order*, not a second per-column flag: the first column is always the
 * record's identifier (desktop's `body-strong` column, phone's line one);
 * the next up to two columns render as phone's line two. This keeps the
 * component's configuration surface to the one thing the spec names —
 * "a column list" — rather than growing a second knob every list has to
 * think about.
 */
export interface DataTableColumn<Row> {
  /** A real field name on the row's generated API type. */
  key: keyof Row & string;
  /**
   * `{typography.numeric}` tabular figures (`font-feature-settings: "tnum" 1`).
   * DESIGN.md § Typography makes this mandatory on "every money column,
   * every date column, every count, and every running balance" — a wider
   * set than columns that are also right-aligned (below), which is why
   * this is its own flag rather than bundled into `align`.
   */
  tabularFigures?: boolean;
  /**
   * Right-aligned (DESIGN.md § Components `data-table-row`: "Numeric
   * columns are right-aligned... everything else is left-aligned").
   * Defaults to `"left"`. A money/count column sets both this and
   * `tabularFigures`; a date column sets only `tabularFigures` — dates
   * carry tabular figures for vertical alignment but read naturally
   * left-aligned, not as a magnitude to compare by place value.
   */
  align?: "left" | "right";
  /** Whether clicking this column's header sorts by it. Defaults to `true`. */
  sortable?: boolean;
  /**
   * Custom cell content. Receives the whole row so a formatted column (a
   * date, a badge) still keys off a real field rather than a derived one.
   * Defaults to the field's raw string value.
   */
  render?: (row: Row) => ReactNode;
  /**
   * Makes this column inline-editable (R-W3, `DESIGN.md`'s `inline-edit-cell`).
   * Absent means read-only, same as every column before Story 2.3. A
   * Read-only user never sees an edit affordance regardless of this
   * (`useIsReadOnly`, the same withholding mechanism Story 2.1 established
   * — not a second one).
   *
   * Story 15.16 widens this from a bare `DataTableEditConfig` to optionally
   * be a function of the row, `(row) => DataTableEditConfig | undefined` —
   * a real, backward-compatible extension: every existing static
   * `edit: {...}` column config keeps working completely unchanged
   * (VC-15.16-07), resolved through `resolveColumnEdit` below at every call
   * site rather than a second, parallel "is this column editable" check.
   * The one real consumer today is `TenantsList.tsx`'s
   * `current_lease_start_date`/`current_lease_end_date` columns: a Tenant
   * row displaying a *Lease's* own dates (Story 15.12) needs a per-row
   * answer — a tenant with no current lease (`current_lease_id === null`)
   * has nothing to edit, while every other row in the same column does.
   * There was no prior mechanism for per-row editability at all — the
   * shared table decided this at the column level only, uniformly for
   * every row — so this is genuinely new capability, not a wiring change.
   */
  edit?: DataTableEditConfigOrResolver<Row>;
}

/** Story 15.16 — see `DataTableColumn.edit`'s own doc comment above for why
 * this exists. Kept as its own named type (not inlined into `edit?:`)
 * purely for readability at the two or three call sites that need to name
 * it explicitly. */
export type DataTableEditConfigOrResolver<Row> =
  | DataTableEditConfig<Row>
  | ((row: Row) => DataTableEditConfig<Row> | undefined);

/**
 * The one place `column.edit`'s two shapes (a static config, or a function
 * of the row) are told apart and resolved to a concrete
 * `DataTableEditConfig | undefined` for a specific row — every call site
 * that used to read `column.edit` directly (`DataTable.tsx`,
 * `InlineEditCell.tsx`, `use-inline-edits.ts`, `lib/inline-edit.ts`'s
 * `nextEditableCell`/`previousEditableCell`) now reads through this instead,
 * so "is this column a function" is decided in exactly one place rather than
 * re-derived at every call site.
 */
export function resolveColumnEdit<Row>(
  column: DataTableColumn<Row>,
  row: Row,
): DataTableEditConfig<Row> | undefined {
  return typeof column.edit === "function" ? column.edit(row) : column.edit;
}

/** A free-text inline-edit cell (`display_name`'s shape). */
export interface DataTableEditTextConfig {
  kind: "text";
}

/** A fixed-option inline-edit cell, rendered as a `<select>` (`role`'s shape). */
export interface DataTableEditSelectConfig {
  kind: "select";
  options: DataTableFilterOption[];
}

/**
 * A named-event transition cell (spec judgement call 3, AD-23): a future
 * status column plugs in here so an inline edit calls the same
 * `transition(entity, event, actor)` shape every other status write in the
 * codebase already goes through, rather than assigning a raw value. Renders
 * the same as `"select"` (a fixed set of options), but `toEvent` — not the
 * raw selected value — is what the commit callback receives
 * (`lib/inline-edit.ts`'s `resolveEditCommit`). Proven only synthetically
 * (VC-2.3-18); no column on `/settings/users` uses this kind, because
 * AD-23's binding list does not include `identity`/`users`.
 */
export interface DataTableEditTransitionConfig<Row> {
  kind: "transition";
  options: DataTableFilterOption[];
  toEvent: (row: Row, value: string) => { event: string; payload: unknown };
}

export type DataTableEditConfig<Row> =
  | DataTableEditTextConfig
  | DataTableEditSelectConfig
  | DataTableEditTransitionConfig<Row>;

export interface DataTableFilterOption {
  value: string;
  label: string;
}

/**
 * One filterable field's UI: a labelled `Select` above the table (its
 * options come from the caller, generally `GET /meta/enums`, AD-2 — this
 * type itself names no domain values) and, once a value is chosen, a
 * removable chip reusing the `saved-view-chip` token (spec judgement call
 * 6). `field` must be one of the API's own `FILTERABLE_FIELDS` — the table
 * does not validate that against the schema; the route wiring it up is
 * responsible for matching the backend's declared set, the same way it is
 * responsible for matching `SORTABLE_FIELDS` via `column.sortable`.
 */
export interface DataTableFilterConfig {
  field: string;
  label: string;
  options: DataTableFilterOption[];
}
